import {defineStore} from "pinia";
import type {FormList, FormLists} from "@t/fd/layout/FormDefinition.ts";
import {injectEnv} from "@fd/Environment.ts";
import {type Ref, ref} from "vue";
import type {Categories, Category} from "@t/list/Category.ts";
import {useUserStore} from "@/store/UserStore.ts";


const UG_CAT = new RegExp("(USERS|GROUPS|ALL)(_(USERS|GROUPS|ALL))*");


export const useListStore = defineStore('list', () => {

	const env = injectEnv();

	const listCache = new Map<string, Ref<FormList>>();
	const categoryCache = new Map<string, Ref<Categories>>();
	const us = useUserStore();


	/**
	 * Install a list to the global list cache
	 * @param id unique ID of the list
	 * @param list	items
	 */
	function installList(id: string, list: FormList):Ref<FormList> {
		let l: Ref<FormList>;
		if (listCache.has(id)) {
			l = listCache.get(id);
			l.value.length = 0;		// erase current list items (will be updated)
		} else {
			l = ref<FormList>([]);
		}
		for (let li of list) l.value.push(li);
		listCache.set(id, l);
		return l;
	}

	function installCategory(id: string, cat: Categories):Ref<Categories> {
		let c: Ref<Categories>;
		if (categoryCache.has(id)) {
			c = categoryCache.get(id);
			c.value.length = 0;		// erase current category items (will be updated)
		} else {
			c = ref<Categories>([]);
		}
		for (let ci of cat) c.value.push(ci);
		categoryCache.set(id, c);
		return c;
	}


	function fillUserList(users: Ref<FormList>, includeInactive: boolean, byName:boolean, ...values: any): void {
		/* { "id": 21, "active": true, "name": "Daniel Mrkvička", "login": "mrkvicka", "access": "STANDARD" } */
		us.allUsers.then((serverList) => {
			for (let u of serverList) {
				if ((u.active && u.allowed) || includeInactive || values.indexOf(u.login) >= 0) {
					users.value.push({
						value: byName ? u.login : u.id.toString(),
						name: u.name,
					});
				}
			}
		});
	}


	function fillUserCategories(cats: Ref<Categories>, includeInactive: boolean, ...values: any): Promise<any> {
		return us.allUsers.then((serverList) => {
			for (let u of serverList) {
				if ((u.active && u.allowed) || includeInactive || values.indexOf(u.login) >= 0) {
					cats.value.push({
						id: u.login,
						title: u.name,
					});
				}
			}
		});

	}

	function fillGroupList(groups: Ref<FormList>, includeInactive: boolean, byName: boolean, ...values: any): void {
		/* { "id": 13, "active": true, "login": "MGMT", "name": "Management", "type": "STANDARD" } */
		us.allGroups.then((serverList) => {
			for (let g of serverList) {
				if ((g.active && g.allowed) || includeInactive || values.indexOf(g.login) >= 0)
					groups.value.push({
						value: byName ? g.login : g.id.toString(),
						name: g.name,
					});
			}
		});
	}

	function fillGroupCategories(cats: Ref<Categories>, includeInactive: boolean, ...values: any): Promise<any> {
		/* { "id": 13, "active": true, "login": "MGMT", "name": "Management", "type": "STANDARD" } */
		return us.allGroups.then((serverList) => {
			for (let g of serverList) {
				if ((g.active && g.allowed) || includeInactive || values.indexOf(g.login) >= 0)
					cats.value.push({
						id: g.login,
						title: g.name,
					});
			}
		});
	}

	/**
	 * @param {string} id of the list
	 * @param values optinal list of values that returned list should contain
	 */
	function findList(id: string, ...values: any):Ref<FormList> {

		if (listCache.has(id)) return listCache.get(id);
		const l = ref<FormList>([]);
		listCache.set(id, l);
		if (id === "USERS") fillUserList(l, false, true, values);
		else if (id === "GROUPS") fillGroupList(l, false, true, values);
		else if (id === "ALL_USERS") fillUserList(l, true, true, values);
		else if (id === "ALL_GROUPS") fillGroupList(l, true, true, values);
		else if (id === "USER_IDS") fillUserList(l, false, false, values);
		else if (id === "GROUP_IDS") fillGroupList(l, false, false, values);
		else if (id === "ALL_USER_IDS") fillUserList(l, true, false, values);
		else if (id === "ALL_GROUP_IDS") fillGroupList(l, true, false, values);
		else {
			env.loadList(id)
				.then((flist) => {
					l.value.length = 0;
					for (let fitem of flist) l.value.push(fitem);
				})
				.catch((err) => {
					console.error("List " + id + " cannot be loaded", err);
					l.value.length = 0;
					l.value.push({value: "", name: "Error loading list " + id})
				});
		}
		return l;
	}

	function findCategory(id: string, ...values: any):Ref<Categories> {

		if (categoryCache.has(id)) return categoryCache.get(id);
		const c = ref<Categories>([]);
		categoryCache.set(id, c);

		if (UG_CAT.test(id)) {
			const parts = id.split("_");
			let includeInactive = false;
			for (let p of parts) {
				if (p === "ALL") includeInactive = true;
				else if (p === "USERS") fillUserCategories(c, includeInactive, values);
				else if (p === "GROUPS") fillGroupCategories(c, includeInactive, values);
			}
		} else {
			env.loadCategories(id)
				.then((cats) => {
					c.value.length = 0;
					for (let citem of cats) c.value.push(citem);
				})
				.catch((err) => {
					console.error("Category " + id + " cannot be loaded", err);
					c.value.length = 0;
					c.value.push({id: "", title: "Error loading cats " + id});
				});
		}
		return c;
	}

	return {
		installList, findList,
		installCategory, findCategory
	}
});
